import '@xyflow/react/dist/style.css';

import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import {
  addEdge,
  Background,
  Connection,
  ConnectionLineType,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  reconnectEdge,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled.js';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useBeforeUnload, useBlocker, useNavigate, useParams } from 'react-router-dom';
// @ts-ignore
import { v4 as uuid } from 'uuid';

import ActionDialog from '~components/ActionDialog';
import AsyncLoader from '~components/AsyncLoader';
import EmptyState from '~components/EmptyState';
import { useSiteLayout } from '~components/SiteLayout';
import {
  createContactFlow,
  getArchivedContactFlows,
  getContactFlow,
  updateContactFlow,
} from '~pages/SipConfiguration/api';
import {
  ContactFlow,
  ContactFlowAction,
  ContactFlowActionsSummary,
  ContactFlowActionType,
  ContactFlowData,
  ContactFlowVersion,
  GetContactFlowDataDecoder,
  GetContactFlowDataEncoder,
} from '~pages/SipConfiguration/domain';
import { useNotification } from '~providers/NotificationProvider';
import Routes from '~providers/RouteProvider/Routes';
import { APIError, UnsupportedStructureError } from '~services/Errors';
import { deepEqual } from '~utils/Functions';

import JSONEditorDialog from './JSONEditorDialog';
import { ContactFlowNode, ContactFlowNodes, CreateableContactFlowNodes } from './Nodes';

const DATE_FORMAT = 'FFF';
const DATE_FORMAT_TAGS = "yyyy-MM-dd'T'HH:mm:ssZZ";
const formatDate = (date?: string | null, formatString?: string) => {
  if (date && formatString) {
    return DateTime.fromISO(date).toFormat(formatString);
  }
  return '-';
};
function actionsToNodesAndEdges(actions: ContactFlowAction[]): { nodes: ContactFlowNode[]; edges: Edge[] } {
  return {
    nodes: actions.map(({ id, type, description, parameter, next }) => ({
      id,
      // fallback to the default node if not a ContactFlowNode
      ...(type in ContactFlowNodes && { type }),
      data: {
        type,
        description,
        parameter: Object.entries(parameter ?? {}).reduce((p, [k, v]) => (v ? { ...p, [k]: v } : p), {}),
        sourceHandles: next?.routes.map(({ selector }, i) => ({
          handleId: `${id}-${i}`,
          selector,
        })),
      },
      position: { x: 0, y: 0 },
    })),
    edges: actions.flatMap(({ id: source, next }) =>
      (next?.routes || []).map(({ actionId: target }, i) => {
        const sourceHandle = `${source}-${i}`;
        return {
          id: `${sourceHandle}-${target}`,
          source,
          target,
          sourceHandle,
        };
      }),
    ),
  };
}

function nodesAndEdgesToActions(nodes: ContactFlowNode[], edges: Edge[]): ContactFlowActionsSummary {
  return {
    actions: nodes.slice(1).map(({ id, data: { type, parameter, description = '', sourceHandles } }) => ({
      id,
      type,
      description,
      parameter,
      next: sourceHandles && {
        routes: sourceHandles.map(({ handleId, selector }) => ({
          actionId: edges.find((e) => e.sourceHandle === handleId)?.target ?? '',
          selector,
        })),
      },
    })),
    startActionId: edges.find(({ source }) => source === nodes[0].id)?.target ?? '',
  };
}

const elk = new ELK();
const layoutOptions = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.layered.spacing.edgeNodeBetweenLayers': '80',
  'elk.spacing.edgeEdge': '60',
  'elk.spacing.nodeNode': '60',
  'elk.spacing.edgeNode': '60',
  'elk.layered.nodePlacement.strategy': 'SIMPLE',
};

async function relayoutNodes(nodes: ContactFlowNode[], edges: Edge[]) {
  const graph = {
    id: 'root',
    layoutOptions,
    children: nodes.map((n) => {
      const sourcePorts = n.data.sourceHandles?.map((s) => ({
        id: s.handleId,
        properties: {
          side: 'EAST',
        },
      }));

      return {
        id: n.id,
        width: n.width ?? n.measured?.width ?? 150,
        height: n.height ?? n.measured?.height ?? 50,

        // ⚠️ we need to tell elk that the ports are fixed to reduce edge crossings
        properties: {
          'org.eclipse.elk.portConstraints': 'FIXED_ORDER',
        },
        // we are also passing the id, so we can also handle edges without a sourceHandle or targetHandle option
        ports: [
          {
            id: n.id,
            properties: {
              side: 'WEST',
            },
          },
          ...(sourcePorts ?? []),
        ],
      };
    }),
    edges: edges.map((e) => ({
      id: e.id,
      targets: [e.targetHandle || e.target],
      sources: [e.sourceHandle || e.source],
    })),
  };

  const layoutedGraph = await elk.layout(graph);

  const layoutedNodes = nodes.map((node) => {
    const layoutedNode = layoutedGraph.children?.find((lgNode) => lgNode.id === node.id);
    return {
      ...node,
      position: {
        x: layoutedNode?.x ?? 0,
        y: layoutedNode?.y ?? 0,
      },
    };
  });

  return layoutedNodes;
}

type ErrorMessage = {
  text: string;
  subText: string;
};
const StartingPointNode: ContactFlowNode = {
  id: 'start-entry-point',
  type: ContactFlowActionType.Entry,
  data: {
    type: ContactFlowActionType.Entry,
    parameter: {},
  },
  position: { x: 0, y: 0 },
};

type LayoutFlowState = 'fetching' | 'should-relayout' | 'relayouting' | 'should-adjust-view' | 'none';

type CurrentRecordOption = {
  lastUpdatedTimestamp: string | null;
  isCurrent: true;
  group: 'Current';
};

type ArchivedRecordOption = ContactFlow & {
  group: 'Archived Records';
  isCurrent?: false;
};

type RecordOption = CurrentRecordOption | ArchivedRecordOption;

function LayoutFlow() {
  const { contactFlowId } = useParams<{ contactFlowId: string | undefined }>();
  const navigate = useNavigate();
  const { pushNotification } = useNotification();
  const contactFlowInfoForm = useForm<ContactFlow>({
    defaultValues: {
      version: ContactFlowVersion,
    },
  });
  const {
    control,
    getValues,
    reset,
    watch,
    handleSubmit,
    formState: { isDirty: isContactFlowInfoDirty },
  } = contactFlowInfoForm;
  const [nodes, setNodes, onNodesChange] = useNodesState<ContactFlowNode>(contactFlowId ? [] : [StartingPointNode]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [initialContactFlowActionsSummary, setInitialContactFlowActionsSummary] = useState<ContactFlowActionsSummary>(
    nodesAndEdgesToActions(nodes, edges),
  );
  const isActionsSummaryDirty = useMemo(
    () => !deepEqual(initialContactFlowActionsSummary, nodesAndEdgesToActions(nodes, edges)),
    [initialContactFlowActionsSummary, nodes, edges],
  );
  const isDirty = isContactFlowInfoDirty || isActionsSummaryDirty;
  const { viewportInitialized, screenToFlowPosition, fitView } = useReactFlow<ContactFlowNode, Edge>();
  const updateNodeInternals = useUpdateNodeInternals();
  const nodesInitialized = useNodesInitialized();
  const [openEditor, setOpenEditor] = useState<boolean>(false);
  const [state, setState] = useState<LayoutFlowState>(contactFlowId ? 'fetching' : 'none');
  const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false);
  const [publish, setPublish] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage>();
  const [recordsLoading, setRecordsLoading] = useState<boolean>(false);
  const [archivedRecords, setArchivedRecords] = useState<ContactFlow[]>([]);
  const lastUpdatedTimestamp = watch('lastUpdatedTimestamp');
  const [publishedTimeStamp, setPublishedTimeStamp] = useState<string | null>(null);
  const [currentContactFlowName, setCurrentContactFlowName] = useState<string>('');
  const [skipArchive, setSkipArchive] = useState<boolean>(false);

  const setContactFlowData = (cf: ContactFlowData | ContactFlow, keepDefaultValues = false) => {
    reset(cf, { keepDefaultValues });
    const { nodes, edges } = actionsToNodesAndEdges(cf.actions);
    // you need to create a new starting point node object
    // else it won't do relayout
    nodes.unshift({ ...StartingPointNode });
    edges.unshift({
      id: StartingPointNode.id,
      source: StartingPointNode.id,
      sourceHandle: StartingPointNode.id,
      target: cf.startActionId,
    });
    setNodes(nodes);
    setEdges(edges);
    return { nodes, edges };
  };

  const loadContactFlow = async (silent = false) => {
    if (contactFlowId) {
      try {
        const cf = await getContactFlow(+contactFlowId);
        setPublishedTimeStamp(cf.lastUpdatedTimestamp);
        setCurrentContactFlowName(cf.name);
        const { nodes, edges } = setContactFlowData(cf);
        setInitialContactFlowActionsSummary(nodesAndEdgesToActions(nodes, edges));
        setState('should-relayout');
        if (!silent) pushNotification('info', 'Loaded current published version');
      } catch (e) {
        if (e instanceof APIError) {
          setError({ text: 'Unable to request data from backend', subText: e.message });
        }
        if (e instanceof UnsupportedStructureError) {
          setError({ text: 'Data from backend Invalid', subText: 'Unable to decode response' });
        }
      }
    }
  };

  useEffect(() => {
    loadContactFlow(true);
  }, [contactFlowId]);

  useEffect(() => {
    if (state == 'should-relayout' && nodesInitialized) {
      console.log('RELAYOUT');
      setState('relayouting');
      (async () => {
        try {
          setNodes(await relayoutNodes(nodes, edges));
          setState('should-adjust-view');
        } catch {
          pushNotification('error', 'Relayout failed! Invalid contact flow.');
        }
      })();
    }
  }, [state, nodesInitialized]);

  useEffect(() => {
    if (state == 'should-adjust-view' && nodesInitialized) {
      setState('none');
      fitView({
        nodes: [StartingPointNode],
        maxZoom: 0.5,
      });
    }
  }, [state, nodesInitialized]);

  const fetchRecords = async () => {
    if (contactFlowId) {
      setRecordsLoading(true);
      try {
        const data = await getArchivedContactFlows(+contactFlowId);
        setArchivedRecords(data);
      } catch (e) {
        if (!(e instanceof APIError && e.status === 404)) {
          pushNotification('error', 'Failed to fetch records');
        }
      } finally {
        setRecordsLoading(false);
      }
    }
  };

  const handleRecordSelect = (version: ContactFlow) => {
    setContactFlowData(version, true);
    setTimeout(() => setState('should-relayout'), 1);
    pushNotification('info', `Loaded version: ${formatDate(version.lastUpdatedTimestamp, DATE_FORMAT || undefined)}`);
  };

  const recordOptions: RecordOption[] = useMemo(() => {
    const currentOption: CurrentRecordOption = {
      lastUpdatedTimestamp: publishedTimeStamp,
      isCurrent: true,
      group: 'Current',
    };
    const filteredRecords: ArchivedRecordOption[] = archivedRecords
      .filter((v) => v.lastUpdatedTimestamp !== publishedTimeStamp)
      .map((v) => ({ ...v, group: 'Archived Records' }));

    // Only include the current option if it has a timestamp (i.e., not a new flow)
    const options = publishedTimeStamp ? [currentOption, ...filteredRecords] : filteredRecords;
    return options as RecordOption[];
  }, [archivedRecords, publishedTimeStamp, currentContactFlowName]);

  const onConnect = (conn: Connection) => {
    setEdges((eds) => addEdge(conn, eds));
    updateNodeInternals([conn.source, conn.target]);
  };
  const onReconnect = (e: Edge, conn: Connection) => setEdges((eds) => reconnectEdge(e, conn, eds));

  const blocker = useBlocker(() => !publish && isDirty);
  useEffect(() => {
    if (blocker.state == 'blocked') {
      if (confirm('Changes you made may not be saved. Would you like to proceed?')) {
        blocker.proceed();
      }
    }
  }, [blocker]);

  useBeforeUnload((e) => {
    if (isDirty) {
      e.preventDefault();

      // Included for legacy support, e.g. Chrome/Edge < 119
      e.returnValue = true;
    }
  });

  const errorDisplay = error && <EmptyState type='error' text={error.text} />;

  const isRestoring = lastUpdatedTimestamp && publishedTimeStamp && lastUpdatedTimestamp !== publishedTimeStamp;

  return (
    <AsyncLoader isLoading={state == 'fetching'} error={errorDisplay}>
      <FormProvider {...contactFlowInfoForm}>
        <ActionDialog
          open={publish}
          title='Are you sure you want to do this?'
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>
                {isRestoring
                  ? 'You are about to restore this contact flow.'
                  : 'You are about to publish this contact flow.'}
              </Typography>
              {contactFlowId && (
                <Box>
                  <FormControlLabel
                    control={<Checkbox checked={skipArchive} onChange={(e) => setSkipArchive(e.target.checked)} />}
                    label='Do not archive current version'
                  />
                  <Typography variant='caption' display='block' sx={{ pl: 4, color: 'red' }}>
                    This option will replace the current version without saving it to the version history.
                  </Typography>
                </Box>
              )}
            </Box>
          }
          primaryActionTitle='Publish'
          onAccept={handleSubmit(async (data) => {
            const cf: ContactFlowData = {
              ...data,
              ...nodesAndEdgesToActions(nodes, edges),
            };

            if (isRestoring) {
              // Create restore time stamp tag
              const fromDate = formatDate(data.lastUpdatedTimestamp, DATE_FORMAT_TAGS);
              const onDate = DateTime.now().toFormat(DATE_FORMAT_TAGS);
              const newRestoreNote = `restored from ${fromDate} on ${onDate}`;
              // Add restore time stamp tag to existing tags
              cf.tags = cf.tags ? `${cf.tags}, ${newRestoreNote}` : newRestoreNote;
            }
            try {
              if (contactFlowId) {
                await updateContactFlow(+contactFlowId, cf, skipArchive);
                pushNotification('success', 'Success updating contact flow');
              } else {
                await createContactFlow(cf);
                pushNotification('success', 'Success creating new contact flow');
              }
              navigate(`${Routes.sipConfiguration.path}?show=ContactFlows`);
            } catch (e) {
              pushNotification('error', 'Invalid contact flow data');
            }
          })}
          onClose={() => {
            setPublish(false);
            setSkipArchive(false);
          }}
        />
        <JSONEditorDialog
          open={openEditor}
          onOpen={(editor) => {
            const cf: ContactFlowData = {
              ...getValues(),
              ...nodesAndEdgesToActions(nodes, edges),
            };
            editor.set(GetContactFlowDataEncoder.runWithException(cf));
          }}
          onSubmit={(editor) => {
            const data = GetContactFlowDataDecoder.run(editor.get());
            if (data.ok) {
              setContactFlowData(data.result, true);
              setTimeout(() => setState('should-relayout'), 1);
              setOpenEditor(false);
            } else {
              pushNotification('error', data.error.message);
            }
          }}
          onClose={() => setOpenEditor(false)}
        />
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              paddingTop: 6,
              paddingBottom: 2,
              paddingLeft: 3,
              paddingRight: 3,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                alignItems: 'end',
              }}>
              <IconButton onClick={() => setShowMoreInfo((p) => !p)} color='info' size='medium' aria-label='More'>
                {showMoreInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                }}>
                <Controller
                  control={control}
                  name='name'
                  defaultValue=''
                  render={({ field }) => (
                    <TextField {...field} fullWidth size='small' variant='outlined' label='Contact Flow Name' />
                  )}
                />
                <Collapse in={showMoreInfo}>
                  <Box sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Controller
                      control={control}
                      name='description'
                      defaultValue=''
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          size='small'
                          variant='outlined'
                          label='Description'
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name='tags'
                      defaultValue=''
                      render={({ field }) => (
                        <Tooltip
                          title='Use commas (,) to separate tags. Tags separated by spaces will be treated as a single tag.'
                          placement='top-start'>
                          <TextField {...field} fullWidth size='small' variant='outlined' label='Tags' />
                        </Tooltip>
                      )}
                    />
                  </Box>
                </Collapse>
              </Box>
              <Autocomplete
                options={recordOptions}
                loading={recordsLoading}
                onOpen={fetchRecords}
                value={recordOptions.find((o) => o.lastUpdatedTimestamp === lastUpdatedTimestamp)}
                disableClearable
                groupBy={(option) => option.group}
                isOptionEqualToValue={(option, value) => option.lastUpdatedTimestamp === value.lastUpdatedTimestamp}
                getOptionLabel={(option) => {
                  const isLatest = option.group === 'Current';
                  return isLatest
                    ? 'Current: Published'
                    : formatDate(option.lastUpdatedTimestamp, DATE_FORMAT) || 'Version History';
                }}
                onChange={(_, v) => {
                  if (v) {
                    if (v.group === 'Current') {
                      loadContactFlow();
                    } else {
                      handleRecordSelect(v);
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Version History'
                    size='small'
                    sx={{ width: 250 }}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const isLatest = option.group === 'Current';
                  return (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant='caption' color='textSecondary'>
                          {isLatest ? 'Current: Published' : formatDate(option.lastUpdatedTimestamp, DATE_FORMAT)}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
              />
              <Button
                disableElevation
                variant='contained'
                color='primary'
                disabled={state != 'none' || !isDirty}
                onClick={() => {
                  setPublish(true);
                }}>
                Publish
              </Button>
              <Button
                disableElevation
                variant='outlined'
                color='primary'
                onClick={() => {
                  navigate(`${Routes.sipConfiguration.path}?show=ContactFlows`);
                }}>
                Back
              </Button>
              <Button variant='outlined' color='primary' onClick={() => setOpenEditor(true)} sx={{ minWidth: 0, p: 1 }}>
                <DataObjectIcon sx={{ fontSize: 18 }} />
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                flexShrink: 1,
                position: 'relative',
                width: '400px',
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                overflow: 'auto',
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  padding: 2,
                }}>
                <Typography style={{ whiteSpace: 'break-spaces' }} variant='body1' component='p' align='left' paragraph>
                  You can drag these nodes to the pane on the right.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {CreateableContactFlowNodes.map(({ title, type }) => {
                    return (
                      <Box
                        draggable
                        key={type}
                        sx={{
                          border: '1px solid',
                          borderRadius: 50,
                          paddingLeft: 2,
                          paddingRight: 2,
                          cursor: 'pointer',
                        }}
                        onDragStart={(event) => {
                          event.dataTransfer.setData('oberon/sip-contact-flow', type);
                          event.dataTransfer.effectAllowed = 'move';
                        }}>
                        {title}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
            <ReactFlow
              fitView
              minZoom={0.2}
              proOptions={{ hideAttribution: true }}
              style={{ backgroundColor: '#F7F9FB' }}
              nodeTypes={ContactFlowNodes}
              nodes={nodes}
              edges={edges}
              defaultEdgeOptions={{
                type: ConnectionLineType.Bezier,
                style: { strokeWidth: 4 },
                focusable: true,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: blue[600],
                },
              }}
              connectionLineType={ConnectionLineType.Bezier}
              connectionLineStyle={{ strokeWidth: 6, stroke: blue[600], strokeDasharray: '12 8' }}
              connectionRadius={30}
              elevateEdgesOnSelect={true}
              onConnect={onConnect}
              onReconnect={onReconnect}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onEdgesDelete={(edges) => {
                updateNodeInternals(edges.map((edge) => [edge.target, edge.source]).flat());
              }}
              reconnectRadius={20}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (viewportInitialized) {
                  const type = e.dataTransfer.getData('oberon/sip-contact-flow');
                  const position = screenToFlowPosition({
                    x: e.clientX,
                    y: e.clientY,
                  });
                  const newNode: ContactFlowNode = {
                    id: uuid(),
                    type,
                    position,
                    data: { type, parameter: {}, isNew: true },
                  };
                  setNodes((nds) => nds.concat(newNode));
                }
              }}>
              <Background />
              <MiniMap
                ariaLabel='Contact Flow Minimap'
                position='bottom-right'
                pannable={true}
                maskColor='rgb(0, 0, 0, 0.4)'
              />
              <Controls position='bottom-left' />
            </ReactFlow>
          </Box>
        </Box>
      </FormProvider>
    </AsyncLoader>
  );
}

export default function ContactFlowDetails() {
  const setLayout = useSiteLayout();
  useEffect(() => {
    setLayout('fullScreen');
    return () => setLayout('default');
  }, []);

  return (
    <ReactFlowProvider>
      <LayoutFlow />
    </ReactFlowProvider>
  );
}
